import React, { useState, useMemo } from 'react';
import { Bell, Megaphone, Info, Star, MessageCircle, Check, Trash2, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

export function NotificationsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const { 
    notices, notifications, markAllAsRead, markNotificationAsRead,
    readNotices, studentEnrollments 
  } = useData();

  // Filtrar avisos relevantes para o aluno
  const filteredNotices = useMemo(() => {
    if (!user) return [];
    
    return (notices || []).filter(n => {
      // Se for aluno, mostrar avisos gerais ou do curso dele
      if (user.type === 'student') {
        const studentCourses = (studentEnrollments || []).map(e => (e.course || e.courseName || '').toLowerCase());
        if (!n.targetCourse || n.targetCourse === 'all') {
          return n.type === 'student';
        }
        return studentCourses.includes(n.targetCourse.toLowerCase());
      }
      // Se for professor, mostrar avisos de professores ou que ele criou
      if (user.type === 'professor') {
        return n.type === 'professor' || n.authorId === (user.enrollmentId || user.email);
      }
      return n.type === user.type;
    });
  }, [notices, user, studentEnrollments]);

  // Unir notificações de sistema com avisos (notices)
  const allNotifications = useMemo(() => {
    // 1. Mapear notificações de sistema
    const sys = (notifications || []).map(n => ({ 
      id: String(n.id).trim(),
      title: n.title,
      message: n.message || n.sub,
      time: n.time,
      type: n.type || 'info',
      source: 'system',
      color: n.color || 'blue'
    }));

    // 2. Mapear avisos acadêmicos
    const aca = filteredNotices.map(n => ({ 
      id: String(n.id).trim(), 
      title: n.title, 
      message: n.sub, 
      time: n.time, 
      type: 'notice', 
      source: 'academic',
      icon: Megaphone,
      color: n.color || 'purple'
    }));

    // 3. Combinar e filtrar
    const combined = [...sys, ...aca];
    const stringReadNotices = (readNotices || []).map(id => String(id).trim());

    const result = combined
      .filter(n => {
        // Se for acadêmica, verifica se está na lista de lidas
        if (n.source === 'academic') {
          return !stringReadNotices.includes(n.id);
        }
        // Se for sistema, ela já vem filtrada do Contexto (remoção física)
        return true; 
      })
      .sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));

    return result;
  }, [notifications, filteredNotices, readNotices]);

  const unreadCount = allNotifications.length;

  const handleMarkAll = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const academicIds = allNotifications
      .filter(n => n.source === 'academic')
      .map(n => String(n.id));
    
    const systemIds = allNotifications
      .filter(n => n.source === 'system')
      .map(n => String(n.id));
    
    markAllAsRead(academicIds, systemIds);
    setIsOpen(false);
  };

  const handleMarkOne = (e, id, source) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    markNotificationAsRead(String(id), source);
  };

  return (
    <div className="relative">
      <button 
        className={`bell-btn ${unreadCount > 0 ? 'has-notif' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          width: 44,
          height: 44,
          borderRadius: 14,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: unreadCount > 0 ? 'var(--purple)' : 'var(--muted)',
          transition: 'all 0.2s ease'
        }}
      >
        <Bell size={20} className={unreadCount > 0 ? 'animate-pulse' : ''} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: -4,
            right: -4,
            background: 'var(--purple)',
            color: 'white',
            fontSize: 10,
            fontWeight: 900,
            minWidth: 18,
            height: 18,
            borderRadius: 9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 4px',
            border: '2px solid #050914',
            boxShadow: '0 0 10px rgba(139,92,246,0.4)'
          }}>
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-[9998]" 
              onClick={() => setIsOpen(false)} 
            />
            <motion.div 
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-3 w-[380px] bg-[#0b1322] border border-[#1d2a42] shadow-2xl rounded-[2rem] z-[9999] overflow-hidden"
              style={{ boxShadow: '0 30px 60px rgba(0,0,0,0.5)' }}
            >
              <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <div>
                  <h3 className="text-lg font-black tracking-tight">Notificações</h3>
                  <p className="text-[10px] uppercase tracking-widest text-muted font-bold">
                    {unreadCount} {unreadCount === 1 ? 'pendente' : 'pendentes'}
                  </p>
                </div>
                {unreadCount > 0 && (
                  <button 
                    onClick={handleMarkAll}
                    className="text-[10px] font-black uppercase tracking-widest text-purple hover:text-white transition-colors flex items-center gap-1.5"
                  >
                    <Check size={12} /> Limpar tudo
                  </button>
                )}
              </div>

              <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                {allNotifications.length > 0 ? (
                  <div className="divide-y divide-white/5">
                    {allNotifications.map((n) => (
                      <div 
                        key={`${n.source}-${n.id}`} 
                        className="p-5 hover:bg-white/[0.03] transition-all group cursor-pointer relative"
                        onClick={(e) => handleMarkOne(e, n.id, n.source)}
                        title="Clique para marcar como lida"
                      >
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Check size={14} className="text-purple" />
                        </div>
                        <div className="flex gap-4 pr-6">
                          <div className={`w-10 h-10 rounded-xl shrink-0 flex items-center justify-center ${
                            n.color === 'green' ? 'bg-green/10 text-green' : 
                            n.color === 'red' ? 'bg-red/10 text-red' : 
                            'bg-purple/10 text-purple'
                          }`}>
                            {n.type === 'notice' ? <Megaphone size={18} /> : 
                             n.type === 'grade' ? <Star size={18} /> :
                             n.type === 'message' ? <MessageCircle size={18} /> :
                             <Info size={18} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-1">
                              <strong className="text-sm font-bold text-white block truncate pr-2">
                                {n.title}
                              </strong>
                              <span className="text-[9px] font-bold text-muted uppercase shrink-0 mt-0.5">
                                {n.time}
                              </span>
                            </div>
                            <p className="text-xs text-muted leading-relaxed line-clamp-2">
                              {n.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-16 px-8 text-center">
                    <div className="w-16 h-16 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-4 text-muted/30">
                      <Bell size={32} />
                    </div>
                    <h4 className="font-bold text-white mb-1">Tudo em dia!</h4>
                    <p className="text-xs text-muted leading-relaxed">
                      Você não tem nenhuma nova notificação no momento.
                    </p>
                  </div>
                )}
              </div>

              <div className="p-4 bg-white/[0.02] border-t border-white/5">
                <button 
                  onClick={() => { window.location.href = '/avisos'; setIsOpen(false); }}
                  className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-black uppercase tracking-widest text-white transition-all border border-white/5"
                >
                  Ver Central de Avisos
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
